/**
 * "Who Are You?" — `/who`, the player chip's "change players" (§ F118; § F201).
 *
 * § F201 (2026-09-30): the list is the accounts SIGNED IN ON THIS DEVICE, and
 * nobody else. It used to be the whole family roster, and picking a name made
 * you that person; now a name only appears here after its owner typed the code
 * from their own email on this device. That keeps the shared-iPad switch Jake
 * relies on without showing the device anyone who has not signed in on it.
 *
 * Seats (§ F119/§ F180) and guests (§ F128) are gone with it: a signed-in
 * device is the claim, and a guest is now somebody who creates an account.
 *
 * It returns you where you pressed it (`?from=`, same-origin only).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, LogOut, UserPlus } from "lucide-react";
import { AppChrome } from "../components/AppChrome";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { APP_COLUMN } from "../lib/column";
import { useAccounts, usePlayer } from "../lib/usePlayer";
import { signOutPlayer, type StoredPlayer } from "../lib/player";
import { fetchRoster, type RosterPlayer } from "../lib/roster";
import { fetchMyTurns, YourTurnPrompt } from "../turn-based/YourTurn";
import type { WireMatchSummary } from "../turn-based/types";
import { sessionColorFor } from "../party/palette";

/** Only same-origin paths come back from `?from=` — never an absolute URL. */
function safeReturn(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/who")) return "/";
  return raw;
}

export default function WhoPage() {
  const accounts = useAccounts();
  const { player, setPlayer } = usePlayer();
  const [faces, setFaces] = useState<Record<string, RosterPlayer>>({});
  const [busy, setBusy] = useState<string | null>(null);
  /** § F179 — head-to-head games waiting on whoever just switched in. */
  const [turns, setTurns] = useState<WireMatchSummary[] | null>(null);
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const back = safeReturn(search.get("from"));

  // Faces for the rows: whatever the player playing now can see.
  useEffect(() => {
    if (!player) return;
    fetchRoster()
      .then((rows) => setFaces(Object.fromEntries(rows.map((r) => [r.id, r]))))
      .catch(() => {});
  }, [player]);

  const pick = useCallback(
    async (account: StoredPlayer) => {
      if (busy) return;
      setBusy(account.id);
      setPlayer(account);
      // § F179 — if a head-to-head game is waiting on them, offer it now.
      const waiting = await fetchMyTurns(account).catch(() => []);
      if (waiting.length > 0) {
        setTurns(waiting);
        setBusy(null);
        return;
      }
      navigate(back, { replace: true });
    },
    [back, busy, navigate, setPlayer]
  );

  const addHref = useMemo(() => `/login?next=${encodeURIComponent(back)}`, [back]);

  return (
    <AppChrome lock column="menu">
      <main
        className="flex-1 min-h-0 flex items-start justify-center p-3 [@media(min-height:700px)]:p-6"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
      >
        <div
          className={`relative bg-white ink-border rounded-block-lg p-4 [@media(min-height:700px)]:p-8 ${APP_COLUMN} block-shadow-2`}
          data-testid="who-picker"
        >
          {turns && turns.length > 0 ? (
            <YourTurnPrompt
              turns={turns}
              onGo={(path) => navigate(path, { replace: true })}
              onSkip={() => navigate(back, { replace: true })}
            />
          ) : (
            <>
              <Link
                to={back}
                aria-label="Back"
                data-testid="who-back"
                className="absolute left-1 top-1 w-11 h-11 inline-flex items-center justify-center text-black/70 btn-press"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={3} />
              </Link>

              <h1 className="font-heading font-extrabold text-2xl [@media(min-height:700px)]:text-3xl tracking-tight text-center mb-3 [@media(min-height:700px)]:mb-4">
                Who Are You?
              </h1>

              <div className="grid grid-cols-1 gap-3">
                {accounts.map((account) => {
                  const you = player?.id === account.id;
                  const face = faces[account.id];
                  const derived = sessionColorFor(account.id);
                  const tone = { hex: face?.color ?? derived.hex };
                  return (
                    <div key={account.id} className="flex gap-2">
                      <button
                        type="button"
                        data-testid={`who-account-${account.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        data-you={you ? "1" : "0"}
                        disabled={!!busy}
                        onClick={() => void pick(account)}
                        style={{ background: tone.hex, color: "#000000" }}
                        className={`flex-1 min-w-0 min-h-[48px] [@media(min-height:700px)]:min-h-[56px] ink-border rounded-block block-shadow-1 btn-press btn-off font-heading font-extrabold uppercase tracking-wide text-base [@media(min-height:700px)]:text-lg px-2 flex items-center gap-2 ${
                          you ? "ring-4 ring-black ring-offset-2 ring-offset-white" : ""
                        }`}
                      >
                        <PlayerAvatar
                          playerId={account.id}
                          avatar={face?.avatar ?? null}
                          color={face?.color ?? null}
                          ink={face?.ink ?? null}
                          size={32}
                        />
                        <span className="min-w-0 flex-1 truncate text-left">{face?.display_name ?? account.name}</span>
                      </button>
                      <button
                        type="button"
                        aria-label={`Sign out ${account.name}`}
                        data-testid="who-sign-out"
                        disabled={!!busy}
                        onClick={() => void signOutPlayer(account)}
                        className="shrink-0 w-12 bg-white ink-border rounded-block btn-press inline-flex items-center justify-center"
                      >
                        <LogOut className="w-5 h-5" strokeWidth={2.5} />
                      </button>
                    </div>
                  );
                })}

                <Link
                  to={addHref}
                  data-testid="who-add-account"
                  className="w-full min-h-[48px] bg-white ink-border rounded-block btn-press font-heading font-extrabold uppercase tracking-wide text-sm px-3 flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-5 h-5" strokeWidth={2.5} />
                  Add another player
                </Link>
              </div>
              <p className="font-body text-xs text-center text-black/70 mt-3">
                Only people who have signed in on this device appear here.
              </p>
            </>
          )}
        </div>
      </main>
    </AppChrome>
  );
}
